import type { CategoryInsight } from '@/lib/domains/insights';
import { formatCurrency } from '@/lib/utils/currency';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { memo } from 'react';

interface CategoryBreakdownProps {
  insights: CategoryInsight[];
  currency: string;
  showComparison?: boolean;
}

/**
 * CategoryBreakdown component - displays category spending insights.
 * Memoized because insights calculation is expensive and doesn't change frequently.
 */
function CategoryBreakdownComponent({
  insights,
  currency,
  showComparison = false
}: CategoryBreakdownProps) {
  if (insights.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        <p>No expenses in this period</p>
        <p className="text-sm mt-2">Start adding expenses to see insights</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {insights.map((insight) => (
        <div
          key={insight.category.id}
          className="bg-white border border-gray-200 rounded-lg p-4"
        >
          {/* Category Header */}
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-3">
              <span
                className="text-3xl"
                style={{ color: insight.category.color }}
              >
                {insight.category.icon}
              </span>
              <div>
                <h3 className="font-semibold text-gray-900">
                  {insight.category.displayName}
                </h3>
                <p className="text-sm text-gray-600">
                  {insight.transactionCount} transaction{insight.transactionCount !== 1 ? 's' : ''} •
                  Avg {formatCurrency(insight.averageAmount, currency)}
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-xl font-bold text-gray-900">
                {formatCurrency(insight.totalSpent, currency)}
              </p>
              <p className="text-sm text-gray-600">
                {insight.percentage.toFixed(1)}%
              </p>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="relative w-full h-3 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="absolute left-0 top-0 h-full rounded-full transition-all duration-300"
              style={{
                width: `${Math.min(insight.percentage, 100)}%`,
                backgroundColor: insight.category.color,
                opacity: 0.8,
              }}
            />
          </div>

          {/* Trend Indicator */}
          {showComparison && insight.trend && (
            <div className="mt-3 flex items-center gap-2 text-sm">
              {insight.trend === 'up' && (
                <>
                  <TrendingUp className="w-4 h-4 text-red-500" />
                  <span className="text-red-600">
                    +{insight.trendPercentage?.toFixed(0)}% vs last month
                  </span>
                </>
              )}
              {insight.trend === 'down' && (
                <>
                  <TrendingDown className="w-4 h-4 text-green-500" />
                  <span className="text-green-600">
                    -{insight.trendPercentage?.toFixed(0)}% vs last month
                  </span>
                </>
              )}
              {insight.trend === 'stable' && (
                <>
                  <Minus className="w-4 h-4 text-gray-500" />
                  <span className="text-gray-600">
                    Similar to last month
                  </span>
                </>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

/**
 * Memoized version prevents re-renders when insights data hasn't changed.
 * Insights array is compared by reference for efficiency.
 */
export const CategoryBreakdown = memo(CategoryBreakdownComponent);
